/**
 * Cart drawer consolidado — portal, atualização AJAX e submodais.
 * Empório Saibai / Ella. Build 2026-08-08.
 */
class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.activeElement = null;
    this._overlayHandler = () => this.close();
    this.addEventListener('keyup', (event) => {
      if (event.code === 'Escape') this.close();
    });
    this.bindOverlay();
    this.bindLegacyCartTriggers();
    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:section:select', (event) => {
        if (event.target.matches('.section-cart-drawer')) this.open();
      });
      document.addEventListener('shopify:section:deselect', (event) => {
        if (event.target.matches('.section-cart-drawer')) this.close();
      });
    }
  }
  connectedCallback() {
    if (this.parentNode !== document.body) {
      this.dataset.moved = 'true';
      document.body.appendChild(this);
      return;
    }
    this.dataset.moved = 'true';
    this.applyGeometry();
    this.bindOverlay();
    window.initCartDrawerHeaderHeight?.();
  }
  applyGeometry() {
    Object.assign(this.style, {
      position: 'fixed',
      inset: '0',
      display: 'flex',
      width: '100vw',
      height: '100dvh',
      minHeight: '100vh',
      margin: '0',
      zIndex: '12000',
      isolation: 'isolate',
      overflow: 'hidden',
      overscrollBehavior: 'contain',
    });
    const overlay = this.querySelector('#CartDrawer-Overlay, [data-cart-drawer-overlay]');
    if (overlay) Object.assign(overlay.style, {
      position: 'fixed',
      inset: '0',
      width: '100vw',
      height: '100dvh',
      margin: '0',
      zIndex: '0',
    });
    const content = this.contentElement();
    if (content) Object.assign(content.style, {
      position: 'relative',
      height: '100dvh',
      maxHeight: '100dvh',
      marginBlock: '0',
      marginInlineStart: 'auto',
      zIndex: '1',
      overflow: 'hidden',
      overscrollBehavior: 'contain',
    });
  }
  bindLegacyCartTriggers() {
    document.querySelectorAll('.cart-icon--drawer').forEach((trigger) => {
      if (trigger.dataset.cartDrawerBound === 'true') return;
      trigger.dataset.cartDrawerBound = 'true';
      trigger.setAttribute('role', 'button');
      trigger.setAttribute('aria-haspopup', 'dialog');
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(trigger);
      });
      trigger.addEventListener('keydown', (event) => {
        if (event.code !== 'Space') return;
        event.preventDefault();
        this.open(trigger);
      });
    });
  }
  bindOverlay() {
    const overlay = this.querySelector('#CartDrawer-Overlay, [data-cart-drawer-overlay]');
    if (!overlay || overlay.dataset.bound === 'true') return;
    overlay.dataset.bound = 'true';
    overlay.addEventListener('click', this._overlayHandler);
  }
  drawerDirection() {
    let direction = this.dataset.drawerDirection || 'right';
    if (window.theme?.config?.rtl) direction = direction === 'left' ? 'right' : 'left';
    return direction;
  }
  contentElement() {
    return this.querySelector('[data-drawer-content]');
  }
  cancelAnimations(element) {
    element?.getAnimations?.().forEach((animation) => animation.cancel());
  }
  prepareOpenAnimation() {
    const content = this.contentElement();
    if (!content) return;
    this.cancelAnimations(content);
    content.style.opacity = '0';
    content.style.transform =
      this.drawerDirection() === 'left' ? 'translateX(-100%)' : 'translateX(100%)';
  }
  nextPaint() {
    return new Promise((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(resolve));
    });
  }
  async animateContent(open) {
    const content = this.contentElement();
    if (!content) return;
    this.cancelAnimations(content);
    const offscreen = this.drawerDirection() === 'left'
      ? 'translateX(-100%)'
      : 'translateX(100%)';
    const motion = typeof Motion !== 'undefined' ? Motion : window.Motion;
    if (!motion?.animate) {
      content.style.opacity = open ? '1' : '0';
      content.style.transform = open ? 'translateX(0)' : offscreen;
      return;
    }
    const animation = motion.animate(
      content,
      {
        opacity: open ? [0, 1] : [1, 0],
        transform: open ? [offscreen, 'translateX(0)'] : ['translateX(0)', offscreen],
      },
      { duration: 0.3, easing: [0.61, 0.22, 0.23, 1] }
    );
    try { await animation.finished; } catch (_) {}
    content.style.opacity = open ? '1' : '';
    content.style.transform = open ? 'translateX(0)' : '';
  }
  async open(triggeredBy, options = {}) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    if (this.parentNode !== document.body) document.body.appendChild(this);
    const content = this.contentElement();
    if (!content) return;
    this.classList.add('open', 'animate', 'active');
    document.body.classList.add('overflow-hidden');
    document.documentElement.setAttribute('scroll-lock', '');
    await this.animateContent(true);
    const focusContainer = this.classList.contains('is-empty')
      ? this.querySelector('.drawer__inner-empty')
      : this.querySelector('#CartDrawer');
    const closeButton = this.querySelector('.drawer__close');
    if (focusContainer && typeof trapFocus === 'function') {
      trapFocus(focusContainer, closeButton || focusContainer);
    }
    if (!options.deferLayoutMeasure) window.scheduleCartDrawerHeaderHeight?.();
    window.initCartDrawerHeaderHeight?.();
    document.dispatchEvent(new CustomEvent('cart-drawer:open', { detail: { drawer: this } }));
  }
  async close() {
    if (!this.classList.contains('active') && !this.classList.contains('open')) return;
    if (typeof removeTrapFocus === 'function') removeTrapFocus(this.activeElement);
    await this.animateContent(false);
    this.classList.remove('open', 'active', 'animate');
    document.body.classList.remove('overflow-hidden');
    document.documentElement.removeAttribute('scroll-lock');
    document.dispatchEvent(new CustomEvent('cart-drawer:close', { detail: { drawer: this } }));
  }
  async renderContents(state) {
    if (!state) return;
    this.productId = state.id;
    const options = { measureHeight: false };
    const recommendations = this.querySelector('.drawer__cart-items-recommendations-wrapper');
    const preserved = recommendations?.innerHTML?.trim() || '';
    const cartItems = this.querySelector('cart-drawer-items');
    const sectionKey = state.sections
      ? Object.keys(state.sections).find((key) => key.includes('cart-drawer'))
      : null;
    await Promise.all(this.getSectionsToRender().map(async (section) => {
      if (section.id === 'cart-icon-bubble') {
        await this.updateCartIconBubble(state);
        return;
      }
      if (section.id === 'cart-drawer' && sectionKey && cartItems?.updateSections) {
        cartItems.updateSections(state.sections, state, options);
        return;
      }
      const target = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      const html = state.sections?.[section.id] || state.sections?.[sectionKey];
      const inner = this.getSectionInnerHTML(html, section.selector);
      if (target && inner != null) target.innerHTML = inner;
    }));
    if (state.item_count !== undefined) {
      window.setCartDrawerEmptyState?.(state.item_count === 0, options);
    }
    if (preserved) {
      const current = this.querySelector('.drawer__cart-items-recommendations-wrapper');
      if (current && !current.querySelector('.cart-drawer__recommendations')) {
        current.innerHTML = preserved;
        current.querySelectorAll('.add-to-cart-button').forEach((button) => {
          button.removeAttribute('aria-disabled');
          button.classList.remove('loading');
          button.querySelector('.loading__spinner')?.classList.add('hidden');
        });
      }
    }
    if (typeof initPurchaseConditions === 'function') initPurchaseConditions(this);
    this.bindOverlay();
    this.prepareOpenAnimation();
    await this.nextPaint();
    await this.open(null, { deferLayoutMeasure: true });
  }
  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return null;
    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    const target = selector ? documentFragment.querySelector(selector) : documentFragment.body;
    return target?.innerHTML ?? null;
  }
  async updateCartIconBubble(state) {
    const html = state.sections?.['cart-icon-bubble'];
    if (html) {
      document.querySelectorAll('#cart-icon-bubble').forEach((bubble) => {
        if (bubble.closest('button, a')?.querySelector('.cart-text')) return;
        const inner = this.getSectionInnerHTML(html);
        if (inner != null) bubble.innerHTML = inner;
      });
    }
    let count = this.itemCountFromSection(html);
    if (count === undefined) count = state.item_count;
    if (count === undefined) {
      try {
        const response = await fetch('/cart.js', { cache: 'no-store' });
        count = (await response.json()).item_count;
      } catch (_) {}
    }
    if (count !== undefined) this.updateCartCounts(count);
  }
  itemCountFromSection(html) {
    if (!html) return undefined;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const text = doc.querySelector('[data-cart-count]')?.textContent?.trim();
    const count = Number.parseInt(text, 10);
    if (!Number.isNaN(count)) return count;
    return text === window.cartStrings?.item_99 ? 100 : undefined;
  }
  updateCartCounts(count) {
    const maxLabel = window.cartStrings?.item_99;
    const value = count < 100 ? String(count) : maxLabel;
    document.querySelectorAll('[data-cart-count]').forEach((element) => {
      element.textContent = element.closest('.cart-text') ? `(${value})` : value;
    });
    document.querySelectorAll('[data-cart-text]').forEach((element) => {
      const template = count === 1 ? window.cartStrings?.item : window.cartStrings?.items;
      if (template) element.textContent = template.replace('[count]', count);
    });
    window.updateCartEmptyStateVisibility?.(count);
  }
  getSectionsToRender() {
    return [
      { id: 'cart-drawer', selector: '#CartDrawer' },
      { id: 'cart-icon-bubble' },
    ];
  }
  setActiveElement(element) {
    this.activeElement = element;
  }
}
if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);
